/**
 * ModalMessageDialog
 *
 * Small popup to show success or error after saving.
 */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

/**
 * ModalMessageDialog
 *
 * Props:
 * - open: boolean (whether the dialog is open)
 * - onClose: function (called when dialog is closed)
 * - type: 'success' | 'error' (type of message)
 * - message: string (message to display)
 */
export default function ModalMessageDialog({
  open,
  onClose,
  type = "success",
  message,
}) {
  const borderColor = type === "success" ? "success.main" : "error.main";
  const bgColor = type === "success" ? "success.50" : "error.50";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box
        sx={{
          border: 2,
          borderRadius: 1,
          borderColor: borderColor,
          bgcolor: bgColor,
          m: 1,
          p: 3,
          boxShadow: 2,
        }}
      >
        <Stack alignItems="center" spacing={1} sx={{ pt: 1 }}>
          {type === "success" ? (
            <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 56, color: "error.main" }} />
          )}
        </Stack>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: borderColor,
            pt: 1,
            pb: 0,
          }}
        >
          {type === "success" ? "Success" : "Error"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                fontSize: "1rem",
                fontWeight: 500,
                color: borderColor,
                textAlign: "center",
              }}
            >
              {message}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 1, pt: 0 }}>
          <Button
            onClick={onClose}
            variant="contained"
            color={type === "success" ? "success" : "error"}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
